import { useState, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { RefreshCw, Lightbulb } from "lucide-react";
import { makeAkari, computeLit, akariConflicts, akariWin } from "@/lib/akari";

export function AkariGame({ n, onWin, resetKey }) {
  const { t } = useTranslation();
  const [puzzle, setPuzzle] = useState(null);
  const [bulb, setBulb] = useState([]);

  const start = useCallback(() => {
    const p = makeAkari(n);
    setPuzzle(p);
    setBulb(Array.from({ length: n }, () => Array(n).fill(false)));
  }, [n]);

  useEffect(() => { start(); }, [start, resetKey]);

  const lit = useMemo(() => (puzzle && bulb.length ? computeLit(bulb, puzzle.wall, n) : []), [bulb, puzzle, n]);
  const conflicts = useMemo(() => (puzzle ? akariConflicts(bulb, puzzle.wall, n) : new Set()), [bulb, puzzle, n]);
  const solved = useMemo(() => (puzzle && bulb.length ? akariWin(bulb, puzzle.wall, puzzle.number, n) : false), [bulb, puzzle, n]);
  useEffect(() => { if (solved) onWin?.(); }, [solved, onWin]);

  const toggle = (r, c) => {
    if (solved || puzzle.wall[r][c]) return;
    setBulb((b) => { const nb = b.map((x) => x.slice()); nb[r][c] = !nb[r][c]; return nb; });
  };

  if (!puzzle || !bulb.length) return null;

  return (
    <div className="flex flex-col items-center" data-testid="akari-game">
      <p className="text-xs text-foreground/45 text-center max-w-md mb-6 leading-relaxed">{t("games.akariRules")}</p>
      <div className="grid gap-1 mx-auto" style={{ gridTemplateColumns: `repeat(${n}, minmax(0,1fr))`, width: "min(86vw, 400px)" }} data-testid="akari-grid">
        {Array.from({ length: n }, (_, r) =>
          Array.from({ length: n }, (_, c) => {
            const isWall = puzzle.wall[r][c];
            const num = puzzle.number[r][c];
            const hasBulb = bulb[r][c];
            const bad = conflicts.has(`${r},${c}`);
            if (isWall) {
              return (
                <div
                  key={`${r}-${c}`}
                  data-testid={`akari-cell-${r}-${c}`}
                  className="aspect-square rounded-md flex items-center justify-center bg-[hsl(24_15%_9%)] border border-white/10 text-foreground/70 font-display text-sm"
                >
                  {num != null ? num : ""}
                </div>
              );
            }
            return (
              <button
                key={`${r}-${c}`}
                data-testid={`akari-cell-${r}-${c}`}
                onClick={() => toggle(r, c)}
                className={`aspect-square rounded-md flex items-center justify-center transition-colors border
                  ${hasBulb ? (bad ? "bg-red-500/25 border-red-500/50" : "bg-primary/25 border-primary/40") : lit[r]?.[c] ? "bg-[hsl(38_80%_55%_/_0.12)] border-white/5" : "bg-white/[0.03] border-white/10 hover:bg-primary/10"}`}
              >
                {hasBulb && <Lightbulb size={16} className={bad ? "text-red-300" : "text-primary"} fill="currentColor" />}
              </button>
            );
          })
        )}
      </div>
      <button onClick={start} data-testid="akari-new-btn" className="mt-7 inline-flex items-center gap-2 text-xs uppercase tracking-wider text-foreground/45 hover:text-primary transition-colors">
        <RefreshCw size={13} /> {t("games.newGrid")}
      </button>
    </div>
  );
}
